import { Fragment, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { NAV_SECTIONS, type NavItem, type NavSection } from './navigation';

/**
 * Which sections the operator has folded away, per browser.
 *
 * Stored as ids of COLLAPSED sections so the default — a first run, a cleared
 * profile, a new section shipping — is always "everything visible". A cashier
 * on day one must be able to find every tab; folding is the experienced
 * user's optimisation, so it is the state that has to be asked for.
 */
const COLLAPSED_KEY = 'gc.staff.nav.collapsed';

function readCollapsed(): readonly string[] {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

function writeCollapsed(ids: readonly string[]): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(ids));
  } catch {
    // Storage refused (private mode, quota): the fold simply won't survive a
    // reload, which costs one tap.
  }
}

function RailLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const label = t(item.translationKey);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      title={label}
      aria-label={label}
      className={({ isActive }) =>
        [
          'group relative flex h-11 w-11 items-center justify-center rounded-control transition-colors',
          // Active = cyan on a faint cyan fill with a live hairline inset —
          // the "powered" treatment from the approved shell card.
          isActive
            ? 'bg-primary/10 text-primary shadow-[inset_0_0_0_1px_var(--border-live)]'
            : 'text-content-secondary hover:bg-hover hover:text-content',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {/*
            Active marker sits on the inline-start edge so it moves to the right
            side automatically under RTL.
          */}
          <span
            aria-hidden="true"
            className={[
              'absolute h-6 w-0.5 rounded-full transition-opacity',
              isActive ? 'bg-primary opacity-100' : 'opacity-0',
            ].join(' ')}
            style={{ insetInlineStart: 0 }}
          />
          <Icon className="h-5 w-5" aria-hidden />
        </>
      )}
    </NavLink>
  );
}

/**
 * The desktop pinch: labeled drawer ⇄ icon rail (never on the phone overlay).
 *
 * Lives INSIDE the sidebar rather than in the top bar so the control is next
 * to the thing it resizes; the top-bar hamburger keeps its one job of showing
 * and hiding the whole column. The panel icons mirror under RTL — the sidebar
 * is on the inline-start edge, which is the right side in Arabic and Kurdish.
 */
function WideToggle({ wide, onToggle }: { wide: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const label = wide ? t('nav.collapseMenu') : t('nav.expandMenu');
  const Icon = wide ? PanelLeftClose : PanelLeftOpen;

  return (
    <button
      type="button"
      onClick={onToggle}
      title={label}
      aria-label={label}
      className={[
        'flex h-11 shrink-0 items-center rounded-control text-content-muted transition-colors hover:bg-hover hover:text-content',
        wide ? 'w-full justify-end px-3' : 'w-11 justify-center',
      ].join(' ')}
    >
      <Icon className="h-5 w-5 rtl:-scale-x-100" aria-hidden />
    </button>
  );
}

/** A drawer row: the icon PLUS its name, at finger height. */
function DrawerLink({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const label = t(item.translationKey);
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        [
          'flex min-h-11 items-center gap-3 rounded-control px-3 text-sm transition-colors',
          isActive
            ? 'bg-primary/10 font-medium text-primary shadow-[inset_0_0_0_1px_var(--border-live)]'
            : 'text-content-secondary hover:bg-hover hover:text-content',
        ].join(' ')
      }
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

/**
 * One titled, foldable group in the drawer (owner, 2026-08-14).
 *
 * The section holding the CURRENT page can never be folded shut: `forcedOpen`
 * overrides a stored fold so the active tab is always on screen — a menu that
 * hides "you are here" fails the first thing a menu is for. The stored
 * preference is not rewritten by that override; leave the page and the fold
 * comes back.
 */
function DrawerSection({
  section,
  items,
  collapsed,
  forcedOpen,
  onToggle,
}: {
  section: NavSection;
  items: readonly NavItem[];
  collapsed: boolean;
  forcedOpen: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const open = !collapsed || forcedOpen;
  const listId = `nav-section-${section.id}`;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={listId}
        className="flex min-h-11 w-full items-center justify-between gap-2 rounded-control px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-content-muted transition-colors hover:bg-hover hover:text-content-secondary"
      >
        {section.translationKey ? t(section.translationKey) : null}
        <ChevronDown
          aria-hidden
          className={[
            'h-4 w-4 shrink-0 transition-transform duration-200',
            // Folded: point along the reading direction, like every file tree.
            open ? '' : '-rotate-90 rtl:rotate-90',
          ].join(' ')}
        />
      </button>

      {open ? (
        <div id={listId} className="flex flex-col gap-1">
          {items.map((item) => (
            <DrawerLink key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <div id={listId} hidden />
      )}
    </div>
  );
}

/**
 * Main navigation, in two shapes.
 *
 * `drawer` is the labeled one: finger-wide rows, sectioned under foldable
 * headings (owner, 2026-08-14: "device list and cafe could be under one
 * category that can be collapsed"). Folds persist per browser; the section
 * with the current page always stays open. On phones it overlays; on desktop
 * it sits inline as the default shape, because headings the owner asked for
 * must be visible without knowing a secret.
 *
 * `rail` is the slim icon column for whoever wants the room back: every
 * section flat, a hairline between groups — an icon column has nowhere honest
 * to put a heading, but the grouping still reads as rhythm.
 *
 * `onToggleWide` (desktop only) renders the pinch control that switches the
 * two; the choice persists per browser (`AppShell` owns it). The phone
 * overlay has no such control — it is always the labeled drawer.
 *
 * Entries the signed-in employee lacks permission for are not rendered at all —
 * hiding rather than disabling, because the owner's mental model is "this
 * employee doesn't have that tab". A section whose every entry is hidden takes
 * its heading with it.
 */
export function Sidebar({
  variant = 'rail',
  onToggleWide,
}: {
  variant?: 'rail' | 'drawer';
  /** Present on desktop: switch between the labeled drawer and the icon rail. */
  onToggleWide?: () => void;
}) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<readonly string[]>(readCollapsed);

  const visibleSections = NAV_SECTIONS.map((section) => ({
    section,
    items: section.items.filter((item) => item.permission === null || can(item.permission)),
  })).filter((entry) => entry.items.length > 0);

  function toggle(id: string): void {
    setCollapsed((current) => {
      const next = current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id];
      writeCollapsed(next);
      return next;
    });
  }

  if (variant === 'drawer') {
    return (
      <nav
        aria-label="Main"
        className={[
          'flex h-full w-64 flex-col gap-1 overflow-y-auto border-e border-subtle bg-raised p-3',
          // The phone overlay floats over content and earns its shadow; the
          // inline desktop shape sits beside it and must not cast one.
          onToggleWide ? '' : 'shadow-2xl',
        ].join(' ')}
      >
        {onToggleWide ? <WideToggle wide onToggle={onToggleWide} /> : null}
        {visibleSections.map(({ section, items }) =>
          section.translationKey === null ? (
            // The ungrouped strip (Dashboard): bare rows, no heading, no fold.
            <div key={section.id} className="flex flex-col gap-1">
              {items.map((item) => (
                <DrawerLink key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <DrawerSection
              key={section.id}
              section={section}
              items={items}
              collapsed={collapsed.includes(section.id)}
              forcedOpen={items.some((item) => pathname.startsWith(item.path))}
              onToggle={() => toggle(section.id)}
            />
          ),
        )}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Main"
      className="flex h-full flex-col items-center gap-1 overflow-y-auto border-e border-subtle bg-raised py-3"
      style={{ width: 'var(--sidebar-width)' }}
    >
      {onToggleWide ? <WideToggle wide={false} onToggle={onToggleWide} /> : null}
      {visibleSections.map(({ section, items }, index) => (
        <Fragment key={section.id}>
          {index > 0 ? <div aria-hidden className="my-1 h-px w-8 shrink-0 bg-subtle" /> : null}
          {items.map((item) => (
            <RailLink key={item.id} item={item} />
          ))}
        </Fragment>
      ))}
    </nav>
  );
}
